/* eslint-disable complexity */
import React, { useState, useEffect, useRef } from 'react';
import FormCard from './FormCard';
import { formatDate } from '../../utils/utils.js';
import Select from 'react-select';
import { customDropdownStyles } from '../../data/data';
import { useApiServiceInference } from '../../services/apiServiceInference.js';
import { formValidationSchemaModels } from '../../schema/formValidationSchema.js';
import ActiveModelsTable from '../Common/ActiveModelsTable.jsx';
import AnomaliesTable from '../Common/AnomaliesTable.jsx';
import InferenceTimeseriesChart from '../../charts/InferenceTimeseriesChart.jsx';
import toast from 'react-hot-toast';
import Loading from '../Common/Loading.jsx';
import ConfirmationPopup from '../Common/ConfirmationPopup.jsx';

const InferenceForm = () => { // NOSONAR
  const queryParams = new URLSearchParams(window.location.search);
  const datasourceParam = queryParams.get('datasource');
  const dgsTagsParams = queryParams.get('dgsTags');
  const modelStartDate = queryParams.get('startDate');
  const modelEndDate = queryParams.get('endDate');

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [activeModels, setActiveModels] = useState([]);
  const [inferenceData, setInferenceData] = useState({});
  const [, setTotalPages] = useState(1);
  // State for selected anomaly (row)
  const [selectedAnomaly, setSelectedAnomaly] = useState(null);

  const [isPopupVisible, setIsPopupVisible] = useState(false);

  // Calculate default dates
  const today = new Date();

  const endDate = new Date(today);
  // 1 day before today.
  endDate.setDate(endDate.getDate() - 1);

  const startDate = new Date(today);
  // 6 months before today.
  startDate.setMonth(startDate.getMonth() - 6);

  // Add state for start and end date
  let defaultStartDate = formatDate(startDate);
  if (modelStartDate) {
    defaultStartDate = modelStartDate;
  }

  let defaultEndDate = formatDate(endDate);
  if (modelEndDate) {
    defaultEndDate = modelEndDate;
  }

  const [startDateState, setStartDateState] = useState(defaultStartDate);
  const [endDateState, setEndDateState] = useState(defaultEndDate);

  const [dropdownOptions, setDropdownOptions] = useState({
    assemblyOptions: [],
    dryGasSealTagsOptions: [],
    tagsOptions: []
  });

  const [selectedValues, setSelectedValues] = useState({
    selectedAssembly: null,
    selectedDryGasSealTags: null,
    selectedTags: []
  });

  const mapAssembly = item => ({
    label: `${item.DRIVER} ${item.PROJECTNAME}`,
    value: `${item.DRIVER} ${item.PROJECTNAME}`,
    datasource: item.DATASOURCE
  });
  const mapDryGasSealTags = item => {
    const depTagAliasArray = item.TAG_ALIAS_LIST.replace(/[[\]']/g, '').split(
      ', '
    );
    const formattedString = depTagAliasArray.map(tag => `${tag}`).join(',');
    return {
      label: formattedString,
      value: formattedString,
      tagName: item.TAG_NAME,
      modelId: item.MODEL_ID,
      tagAliasList: item.TAG_ALIAS_LIST
    };
  };
  const mapTags = tagAlias => ({
    label: tagAlias,
    value: tagAlias
  });

  const getUniqueOptions = options =>
    Array.from(new Set(options.map(option => option.value))).map(value =>
      options.find(option => option.value === value)
    );

  const {
    getAssemblies,
    getDryGasSealTagsSelection,
    getTagsPaginationInfo,
    getTagsCombination,
    getFilteredTagsPaginationInfo,
    getTags,
    getActiveModels
  } = useApiServiceInference();

  const normalizeTagsResponse = data => {
    if (Array.isArray(data)) {
      const tag_aliases = data.map(tag => tag.tag_alias);
      const timestamps = data[0]?.timestamps || [];
      const values = {};
      const anomalies = {};
      const alarm_anomalies = {};

      tag_aliases.forEach((alias, idx) => {
        values[alias] = data[idx].values || [];
        anomalies[alias] = data[idx].anomalies || [];
        alarm_anomalies[alias] =
          data[idx].alarm_anomalies || data[idx].alaram_anomalies || [];
      });

      return { tag_aliases, timestamps, values, anomalies, alarm_anomalies };
    }

    return data ?? {};
  };

  const mergeInferenceData = (currentData, nextData) => {
    const safeCurrent = currentData ?? {};
    const safeNext = nextData ?? {};
    const currentAliases = safeCurrent.tag_aliases ?? [];
    const nextAliases = safeNext.tag_aliases ?? [];
    const mergedAliases = Array.from(new Set([...currentAliases, ...nextAliases]));

    const mergedValues = { ...(safeCurrent.values ?? {}) };
    const mergedAnomalies = { ...(safeCurrent.anomalies ?? {}) };
    const currentAlarmAnomalies =
      safeCurrent.alarm_anomalies ?? safeCurrent.alaram_anomalies ?? {};
    const nextAlarmAnomalies =
      safeNext.alarm_anomalies ?? safeNext.alaram_anomalies ?? {};
    const mergedAlarmAnomalies = { ...currentAlarmAnomalies };

    nextAliases.forEach(alias => {
      const previousValues = mergedValues[alias] ?? [];
      const previousAnomalies = mergedAnomalies[alias] ?? [];
      const previousAlarmAnomalies = mergedAlarmAnomalies[alias] ?? [];
      const nextValues = safeNext.values?.[alias] ?? [];
      const nextAnomalies = safeNext.anomalies?.[alias] ?? [];
      const nextAlarmValues = nextAlarmAnomalies[alias] ?? [];

      mergedValues[alias] = [...previousValues, ...nextValues];
      mergedAnomalies[alias] = [...previousAnomalies, ...nextAnomalies];
      mergedAlarmAnomalies[alias] = [
        ...previousAlarmAnomalies,
        ...nextAlarmValues
      ];
    });

    return {
      ...safeCurrent,
      ...safeNext,
      tag_aliases: mergedAliases,
      timestamps: [...(safeCurrent.timestamps ?? []), ...(safeNext.timestamps ?? [])],
      values: mergedValues,
      anomalies: mergedAnomalies,
      alarm_anomalies: mergedAlarmAnomalies
    };
  };

  const fetchPaginatedData = async (fetchPaginationInfo, fetchPage) => {
    const paginationInfo = await fetchPaginationInfo();
    const pages = Math.max(1, Number(paginationInfo?.total_pages) || 1);
    setTotalPages(pages);

    const fetchInferencePageWithRetry = async (page, retries = 1) => {
      try {
        const pageDataRaw = await fetchPage(page);
        return {
          page,
          pageData: normalizeTagsResponse(pageDataRaw)
        };
      } catch (error) {
        if (retries > 0) {
          console.warn(`Retrying inference page ${page} after failure.`, error);
          return fetchInferencePageWithRetry(page, retries - 1);
        }
        throw error;
      }
    };

    const BATCH_SIZE = 3;
    const allPageNumbers = Array.from({ length: pages }, (_, index) => index + 1);
    const successfulPageResults = [];
    const failedPages = [];

    for (let index = 0; index < allPageNumbers.length; index += BATCH_SIZE) {
      const batchPages = allPageNumbers.slice(index, index + BATCH_SIZE);
      const settledBatch = await Promise.allSettled(
        batchPages.map(page => fetchInferencePageWithRetry(page))
      );

      settledBatch.forEach((result, resultIndex) => {
        const page = batchPages[resultIndex];
        if (result.status === 'fulfilled') {
          successfulPageResults.push(result.value);
        } else {
          failedPages.push(page);
          console.error(`Inference page ${page} failed:`, result.reason);
        }
      });
    }

    if (successfulPageResults.length === 0) {
      throw new Error('Failed to fetch inference data for all pages.');
    }

    if (failedPages.length > 0) {
      toast.error(
        `Some inference pages could not be loaded (${failedPages.length}/${pages}). Showing partial data.`
      );
    }

    const orderedResults = successfulPageResults.sort((a, b) => a.page - b.page);

    let mergedData = {};
    orderedResults.forEach(({ pageData }) => {
      mergedData = mergeInferenceData(mergedData, pageData);
    });

    setInferenceData({ ...mergedData });
    if (Array.isArray(mergedData.tag_aliases)) {
      setDropdownOptions(prev => ({
        ...prev,
        tagsOptions: mergedData.tag_aliases.map(mapTags)
      }));
    }

    return mergedData;
  };

  const fetchPaginatedInferenceData = (from, to, modelName) =>
    fetchPaginatedData(
      () => getTagsPaginationInfo(from, to, modelName),
      page => getTagsCombination(from, to, modelName, page)
    );

  const fetchPaginatedFilteredInferenceData = (from, to, modelName, tagAlias) =>
    fetchPaginatedData(
      () => getFilteredTagsPaginationInfo(from, to, modelName, tagAlias),
      page => getTags(from, to, modelName, tagAlias, page)
    );

  const fetchDropdownData = async (
    dropdownName,
    apiCall,
    mapFunction,
    datasource = null
  ) => {
    try {
      const data = await apiCall(datasource);

      if (dropdownName === 'tags' && data && Array.isArray(data.tag_aliases)) {
        const options = data.tag_aliases.map(mapFunction);
        setDropdownOptions(prev => ({
          ...prev,
          tagsOptions: options
        }));
        setInferenceData(data ?? {});
      }

      if (Array.isArray(data)) {
        const options = data.map(mapFunction);
        const uniqueOptions = getUniqueOptions(options);
        setDropdownOptions(prev => ({
          ...prev,
          [`${dropdownName}Options`]: uniqueOptions
        }));
        return data;
      }

      if (data && typeof data === 'object') {
        // For inferenceData, set as object directly
        if (dropdownName === 'tags') {
          setInferenceData(data ?? {});
        }
        return data;
      }

      return null;
    } catch (error) {
      toast.error(
        `Error fetching ${dropdownName} data. Please try again later.`
      );
      console.error(
        `Error fetching ${dropdownName} data in Models Screen:`,
        error
      );
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (datasourceParam && dgsTagsParams) {
          const [assembliesData, dryGasSealTagsData] = await Promise.all([
            getAssemblies(),
            getDryGasSealTagsSelection(datasourceParam)
          ]);

          const assemblyOptions = getUniqueOptions(
            (assembliesData ?? []).map(mapAssembly)
          );
          const dryGasSealTagsOptions = getUniqueOptions(
            (dryGasSealTagsData ?? []).map(mapDryGasSealTags)
          );

          setDropdownOptions(prev => ({
            ...prev,
            assemblyOptions,
            dryGasSealTagsOptions
          }));

          const defaultAssembly = assemblyOptions.find(
            option =>
              String(option.datasource).trim() === String(datasourceParam).trim()
          );
          const defaultDryGasSealTag = dryGasSealTagsOptions.find(
            option => option.value === dgsTagsParams
          );

          setSelectedValues(prev => ({
            ...prev,
            selectedAssembly: defaultAssembly ?? null,
            selectedDryGasSealTags: defaultDryGasSealTag ?? null
          }));

          // Don't fetch inference data on initial load - wait for button click
        } else {
          await fetchDropdownData('assembly', getAssemblies, mapAssembly);
        }
      } catch (e) {
        toast.error('Error loading inference filters. Please try again later.');
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const validateField = (fieldName, value) => {
    formValidationSchemaModels
      .validateAt(fieldName, { [fieldName]: value })
      .then(() => {
        setErrors(prevErrors => ({ ...prevErrors, [fieldName]: null }));
      })
      .catch(validationError => {
        setErrors(prevErrors => ({
          ...prevErrors,
          [fieldName]: validationError.message
        }));
      });
  };

  const handleSelectChange = async (name, selectedOption) => { // NOSONAR
    if (name === 'selectedAssembly') {
      setSelectedValues(prev => ({
        ...prev,
        selectedAssembly: selectedOption,
        selectedDryGasSealTags: null,
        selectedTags: []
      }));
    } else if (name === 'selectedDryGasSealTags') {
      setSelectedValues(prev => ({
        ...prev,
        selectedDryGasSealTags: selectedOption,
        selectedTags: []
      }));
    } else {
      setSelectedValues(prev => ({ ...prev, [name]: selectedOption }));
    }

    const fieldsToValidate = ['selectedAssembly', 'selectedDryGasSealTags'];
    if (fieldsToValidate.includes(name)) {
      validateField(name, selectedOption);
    }

    if (name === 'selectedAssembly' && selectedOption) {
      const datasource = selectedOption.datasource;
      setLoading(true);
      try {
        await fetchDropdownData(
          'dryGasSealTags',
          getDryGasSealTagsSelection,
          mapDryGasSealTags,
          datasource
        );
      } catch (e) {
        toast.error(
          'Error fetching Dry Gas Seal Tags. Please try again later.',
          e
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const debounceTimeout = useRef(null);

  // Remove automatic API calls on date changes
  useEffect(() => {
    // Clear any pending debounced calls when component unmounts
    const timeoutId = debounceTimeout.current;
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const handleViewInference = async () => {
    // Validate required fields
    if (!selectedValues.selectedAssembly) {
      toast.error('Please select an Assembly');
      return;
    }
    if (!selectedValues.selectedDryGasSealTags) {
      toast.error('Please select Dry Gas Seal Tags');
      return;
    }

    setLoading(true);
    try {
      const modelName = selectedValues.selectedDryGasSealTags.modelId;
      const tagAliasList = JSON.parse(
        selectedValues.selectedDryGasSealTags.tagAliasList.replace(/'/g, '"')
      );
      const datasource = selectedValues.selectedAssembly.datasource;

      let fetchedTags = {};
      if (
        selectedValues.selectedTags &&
        selectedValues.selectedTags.length > 0
      ) {
        const tagAlias = selectedValues.selectedTags.map(
          option => option.value
        );
        fetchedTags = await fetchPaginatedFilteredInferenceData(
          startDateState,
          endDateState,
          modelName,
          tagAlias
        );
      } else {
        fetchedTags = await fetchPaginatedInferenceData(
          startDateState,
          endDateState,
          modelName
        );
      }

      const activeModels = await getActiveModels(tagAliasList, datasource);

      setInferenceData(fetchedTags ?? {});
      setActiveModels(activeModels);

      if (
        !fetchedTags ||
        !fetchedTags.tag_aliases ||
        fetchedTags.tag_aliases.length === 0
      ) {
        setIsPopupVisible(true);
        setInferenceData({});
      }
    } catch (e) {
      console.log(e);
      toast.error('Error fetching inference data. Please try again later.');
      setInferenceData({});
    } finally {
      setLoading(false);
    }
  };

  const hasActiveModelSelection =
    activeModels.length !== 0 &&
    selectedValues.selectedAssembly &&
    selectedValues.selectedDryGasSealTags;

  const hasInferenceTags =
    inferenceData &&
    inferenceData.tag_aliases &&
    inferenceData.tag_aliases.length !== 0 &&
    selectedValues.selectedDryGasSealTags;

  const canViewInference =
    selectedValues.selectedAssembly &&
    selectedValues.selectedDryGasSealTags;
  let viewInferenceButtonClassName = 'button-primary';
  if (!canViewInference) {
    viewInferenceButtonClassName = 'button-primary opacity-50 cursor-not-allowed';
  }

  return (
    <>
      <div className="flex justify-center flex-col gap-10 items-center mt-4 mb-20">
        {loading && <Loading />}
        <FormCard>
          <div>
            <span className="font-semibold text-2xl text-btn-primary">
              Inference
            </span>
            <p className="mt-3 text-[14.5px]">
              Select a model to observe its generated inference comparison with
              the ground truth data in the selected time period.
            </p>
            <form action="" className="mt-8">
              {/* assembly, dgs tags */}
              <div className="flex gap-4 flex-col">
                <div className="flex gap-4">
                  <div className="flex flex-col w-full">
                    <label htmlFor="assembly" className="mb-1">
                      Assembly*
                    </label>
                    <Select
                      id="assembly"
                      className="w-full"
                      placeholder="Select Assembly"
                      options={(dropdownOptions.assemblyOptions ?? []).filter(
                        opt =>
                          opt.value !== null &&
                          opt.value !== undefined &&
                          opt.value !== ''
                      )}
                      isClearable
                      isSearchable
                      required
                      styles={customDropdownStyles}
                      value={selectedValues.selectedAssembly}
                      onChange={option =>
                        handleSelectChange('selectedAssembly', option)
                      }
                    />
                    {errors.selectedAssembly && (
                      <p className="text-red-500 text-[14.5px] mt-1">
                        {errors.selectedAssembly}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col w-full">
                    <label htmlFor="dgs-tags" className="mb-1">
                      Dry Gas Seal Tags*
                    </label>
                    <Select
                      id="dgs-tags"
                      placeholder="Select Dry Gas Seal Tags"
                      isClearable
                      isSearchable
                      required
                      styles={customDropdownStyles}
                      options={(
                        dropdownOptions.dryGasSealTagsOptions ?? []
                      ).filter(
                        opt =>
                          opt.value !== null &&
                          opt.value !== undefined &&
                          opt.value !== ''
                      )}
                      value={selectedValues.selectedDryGasSealTags}
                      onChange={option =>
                        handleSelectChange('selectedDryGasSealTags', option)
                      }
                    />
                    {errors.selectedDryGasSealTags && (
                      <p className="text-red-500 text-[14.5px] mt-1">
                        {errors.selectedDryGasSealTags}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col w-full">
                    <label htmlFor="dgs-tags" className="mb-1">
                      Tags
                    </label>
                    <Select
                      id="dgs-tags"
                      placeholder="Select the Tags"
                      isClearable
                      isSearchable
                      isMulti
                      styles={customDropdownStyles}
                      options={(dropdownOptions.tagsOptions ?? []).filter(
                        opt =>
                          opt.value !== null &&
                          opt.value !== undefined &&
                          opt.value !== ''
                      )}
                      value={selectedValues.selectedTags}
                      onChange={option =>
                        handleSelectChange('selectedTags', option)
                      }
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex flex-col mt-2  w-full">
                    <label htmlFor="start-date" className="mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="start-date"
                      className="custom-input"
                      value={startDateState}
                      onChange={e => setStartDateState(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col mt-2  w-full">
                    <label htmlFor="end-date" className="mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="end-date"
                      className="custom-input "
                      value={endDateState}
                      onChange={e => setEndDateState(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex w-full gap-2 justify-start mt-6">
                  <button
                    type="button"
                    onClick={handleViewInference}
                    className={viewInferenceButtonClassName}
                    disabled={!canViewInference}
                  >
                    View Inference
                  </button>
                </div>
              </div>
            </form>
          </div>
        </FormCard>
        {hasActiveModelSelection && <ActiveModelsTable data={activeModels} />}
        {hasInferenceTags && (
          <InferenceTimeseriesChart
            inferenceData={inferenceData}
            selectedAnomaly={selectedAnomaly}
          />
        )}
        {hasInferenceTags && (
          <AnomaliesTable
            data={inferenceData}
            selectedTags={selectedValues.selectedDryGasSealTags}
            onRowSelect={setSelectedAnomaly}
          />
        )}
      </div>

      {isPopupVisible && (
        <ConfirmationPopup
          message={'No inference available for this time range.'}
          onConfirm={() => setIsPopupVisible(false)}
          onCancel={() => setIsPopupVisible(false)}
          cancelButtonMsg={''}
          confirmButtonMsg={'OK'}
        />
      )}
    </>
  );
};

export default InferenceForm;
